import type { User } from './fetch-user';
import type { SessionState } from './get-session-from-redis';

type ActiveDashboard = {
  organization: string | null;
  team: string | null;
};

/**
 * Session data returned to routes.
 */
export interface Session {
  user: User;
  sessionState?: SessionState;
  /** From the role-specific core-api V3 release flag cached in Redis. */
  v3Enabled?: boolean;
}

/**
 * The active org+team is whatever the session says it is — nothing else.
 *
 * There used to be a `user.teams[0]` fallback here so an org-scoped feature
 * flag could be evaluated before a team was picked. It silently auto-selected
 * a team for researchers who belong to several, which suppressed the
 * `/researchers/select-team` picker (the legacy app shows its equivalent team
 * switcher on `/dashboard` whenever no team has been chosen). Selection is now
 * always required first: `persistActiveDashboard` evaluates the researcher-V3
 * flag at the moment a team is chosen, so there is no window where a flag
 * needs an org the user has not picked.
 */
export const dashboardFromSession = (
  session: Maybe<Pick<Session, 'sessionState'>>,
): ActiveDashboard => {
  const sessionDashboard = session?.sessionState?.activeDashboard;
  const organization =
    typeof sessionDashboard?.organization === 'string'
      ? sessionDashboard?.organization
      : sessionDashboard?.organization?.id;

  const team =
    typeof sessionDashboard?.team === 'string'
      ? sessionDashboard?.team
      : sessionDashboard?.team?.id;

  if (organization && team) {
    return { organization, team };
  }

  return { organization: null, team: null };
};

export type { ActiveDashboard };
